import { Filter, X } from "lucide-react";
import Popover from "./Popover";

/* Barre de filtres pilotée par des définitions :
   { key, label, type: "select" | "multi" | "text" | "date" | "bool", options: [{value,label}] }
   `values` est un objet plat { [key]: valeur }, vide = filtre inactif. */

function labelFor(def, value) {
  if (value === undefined || value === null || value === "" || (Array.isArray(value) && !value.length)) return null;
  switch (def.type) {
    case "multi": {
      const names = value.map(v => def.options.find(o => o.value === v)?.label || v);
      return names.length > 2 ? `${names.slice(0, 2).join(", ")} +${names.length - 2}` : names.join(", ");
    }
    case "select":
      return def.options.find(o => o.value === value)?.label || String(value);
    case "bool":
      return value === "true" || value === true ? "Oui" : "Non";
    default:
      return String(value);
  }
}

function FilterBody({ def, value, onChange, close }) {
  switch (def.type) {
    case "multi": {
      const current = Array.isArray(value) ? value : [];
      const toggle = v => onChange(current.includes(v) ? current.filter(x => x !== v) : [...current, v]);
      return (
        <div className="adm-filter-pop">
          {def.options.map(opt => (
            <label key={opt.value} className="adm-check">
              <input
                type="checkbox"
                checked={current.includes(opt.value)}
                onChange={() => toggle(opt.value)}
                data-testid={`filter-${def.key}-${opt.value}`}
              />
              <span>{opt.label}</span>
            </label>
          ))}
          <div className="adm-filter-actions">
            <button type="button" className="adm-btn is-sm is-ghost" onClick={() => onChange([])}>Tout décocher</button>
            <button type="button" className="adm-btn is-sm" onClick={close}>Appliquer</button>
          </div>
        </div>
      );
    }
    case "select":
      return (
        <div className="adm-filter-pop">
          {def.options.map(opt => (
            <button
              key={opt.value}
              type="button"
              className={`adm-menu-item${value === opt.value ? " is-active" : ""}`}
              onClick={() => { onChange(opt.value); close(); }}
              data-testid={`filter-${def.key}-${opt.value}`}
            >
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      );
    case "bool":
      return (
        <div className="adm-filter-pop">
          {[{ value: "true", label: "Oui" }, { value: "false", label: "Non" }].map(opt => (
            <button
              key={opt.value}
              type="button"
              className={`adm-menu-item${String(value) === opt.value ? " is-active" : ""}`}
              onClick={() => { onChange(opt.value); close(); }}
            >
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      );
    case "date":
      return (
        <div className="adm-filter-pop">
          <label className="adm-label" htmlFor={`f-${def.key}`}>{def.label}</label>
          <input
            id={`f-${def.key}`}
            type="date"
            className="adm-input"
            value={value || ""}
            onChange={e => onChange(e.target.value)}
            data-testid={`filter-${def.key}-input`}
          />
          <div className="adm-filter-actions">
            <button type="button" className="adm-btn is-sm is-ghost" onClick={() => onChange("")}>Effacer</button>
            <button type="button" className="adm-btn is-sm" onClick={close}>Appliquer</button>
          </div>
        </div>
      );
    default:
      return (
        <div className="adm-filter-pop">
          <label className="adm-label" htmlFor={`f-${def.key}`}>{def.label}</label>
          <input
            id={`f-${def.key}`}
            type="text"
            className="adm-input"
            value={value || ""}
            placeholder={def.placeholder}
            onChange={e => onChange(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") close(); }}
            data-testid={`filter-${def.key}-input`}
          />
          <div className="adm-filter-actions">
            <button type="button" className="adm-btn is-sm is-ghost" onClick={() => onChange("")}>Effacer</button>
            <button type="button" className="adm-btn is-sm" onClick={close}>Appliquer</button>
          </div>
        </div>
      );
  }
}

export default function Filters({ filters = [], values = {}, onChange, onClear }) {
  if (!filters.length) return null;
  const active = filters.filter(def => labelFor(def, values[def.key]) !== null);

  const set = (key, value) => onChange({ ...values, [key]: value });
  const clearOne = key => {
    const next = { ...values };
    delete next[key];
    onChange(next);
  };

  return (
    <div className="adm-filters" data-testid="filters">
      {filters.map(def => {
        const shown = labelFor(def, values[def.key]);
        return (
          <span className="adm-chip-wrap" key={def.key}>
            <Popover
              testid={`filter-${def.key}`}
              title={def.label}
              chevron={false}
              triggerClass={`adm-chip${shown ? " is-set" : ""}`}
              align="left"
              label={
                <>
                  {!shown && <Filter size={12} />}
                  {def.label}
                  {shown && <b>{shown}</b>}
                </>
              }
            >
              {({ close }) => (
                <FilterBody def={def} value={values[def.key]} onChange={v => set(def.key, v)} close={close} />
              )}
            </Popover>
            {shown && (
              <button
                type="button"
                className="adm-chip-clear"
                aria-label={`Retirer le filtre ${def.label}`}
                onClick={() => clearOne(def.key)}
                data-testid={`filter-clear-${def.key}`}
              >
                <X size={12} />
              </button>
            )}
          </span>
        );
      })}
      {active.length > 1 && (
        <button type="button" className="adm-btn is-sm is-ghost" onClick={onClear} data-testid="filters-clear-all">
          Tout effacer
        </button>
      )}
    </div>
  );
}
